import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { OtpInput } from "components/ui/otp-input";
import { Spinner } from "components/ui/spinner";
import routes from "routes/routes";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/useDispatch";
import {
  useConfirmOtpMutation,
  useRequestOtpMutation,
} from "api/account/accountApiSlice";
import { AccountApiError } from "api/account/types";
import {
  clearAccountLoginEmail,
  setAccountProfile,
} from "../../../redux/slice/account/accountSlice";
import { setAccountSessionToken } from "authentication/accountSession";
import AccountPageLayout from "../components/AccountPageLayout";
import { readReturnTo, withReturnTo } from "../common/returnTo";

const RESEND_COOLDOWN_SECONDS = 30;

/** Error codes the OTP confirm endpoint may return for non-retryable states. */
const EXPIRED_ERROR_CODES = ["code_expired", "expired", "too_many_attempts"];

/**
 * The account is locked (#2102 item 5). The server checks this AFTER the code
 * verifies, deliberately: `POST /auth/otp/request` answers 204 for every
 * address so it never reveals whether one has an account here, and refusing at
 * that step would leak exactly that. So the state is disclosed only once the
 * person has proved control of the mailbox — which is right, but it used to
 * arrive as "the code is incorrect", telling someone their correct code was
 * wrong and leaving them to retry forever.
 */
const ACCOUNT_LOCKED_ERROR_CODE = "account_locked";

/**
 * H-3a account login — code step. The email lives in Redux flow state only
 * (never in the URL); on refresh / deep link without it the page redirects
 * back to the email step. On success the session token is stored via the
 * accountSession module and the user lands on the validated returnTo (or the
 * account page).
 */
const AccountLoginVerifyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const email = useSelector((state: RootState) => state.account.loginEmail);
  const [confirmOtp, { isLoading: isVerifying }] = useConfirmOtpMutation();
  const [requestOtp, { isLoading: isResending }] = useRequestOtpMutation();

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Set before the success dispatches clear the flow email, so the
  // missing-email redirect below doesn't race the success navigation.
  const completedRef = useRef(false);

  const returnTo = readReturnTo(location.search);

  useEffect(() => {
    if (completedRef.current) return;
    if (!email) {
      navigate(withReturnTo(routes.accountLoginPath, returnTo), {
        replace: true,
      });
    }
  }, [email, navigate, returnTo]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timerId = window.setTimeout(
      () => setResendCooldown((seconds) => Math.max(0, seconds - 1)),
      1000
    );
    return () => window.clearTimeout(timerId);
  }, [resendCooldown]);

  const handleCodeChange = (nextValue: string) => {
    setCode(nextValue);
    setCodeError(null);
  };

  const handleVerify = async () => {
    if (!email) return;
    if (code.length !== 6) {
      setCodeError(t("account.verify.error_invalid_length"));
      return;
    }

    try {
      const response = await confirmOtp({ email, code }).unwrap();
      completedRef.current = true;
      setAccountSessionToken(response.token);
      dispatch(setAccountProfile(response.profile));
      dispatch(clearAccountLoginEmail());
      navigate(returnTo ?? routes.accountPath, { replace: true });
    } catch (error) {
      const apiError = error as AccountApiError;
      const errorCode = apiError?.data?.code?.toLowerCase();
      const isLocked =
        errorCode === ACCOUNT_LOCKED_ERROR_CODE || apiError?.status === 403;
      const isExpiredOrThrottled =
        (errorCode !== undefined && EXPIRED_ERROR_CODES.includes(errorCode)) ||
        apiError?.status === 410 ||
        apiError?.status === 429;
      // Locked is checked FIRST: it is a 403 carrying a code, and the
      // catch-all below would otherwise claim a verified code was wrong.
      setCodeError(
        isLocked
          ? t("account.verify.error_account_locked")
          : isExpiredOrThrottled
            ? t("account.verify.error_expired")
            : t("account.verify.error_wrong_code")
      );
    }
  };

  // While the cooldown runs the button counts down; otherwise it invites a
  // resend. (Kept out of the JSX so the button body stays a single ternary.)
  const resendLabel =
    resendCooldown > 0
      ? t("account.verify.resend_wait", { seconds: resendCooldown })
      : t("account.verify.resend");

  const handleResend = async () => {
    if (!email || isResending || resendCooldown > 0) return;
    setResendMessage(null);
    setResendError(null);

    try {
      await requestOtp({ email }).unwrap();
      setResendMessage(t("account.verify.resend_success"));
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setResendError(t("account.verify.resend_error"));
    }
  };

  if (!email) {
    // Redirect to the email step is in flight (effect above).
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <AccountPageLayout title={t("account.verify.title")}>
      <p className="text-center text-sm text-slate-600">
        {t("account.verify.description", { email })}
      </p>

      <div className="space-y-2">
        <OtpInput
          value={code}
          onChange={handleCodeChange}
          length={6}
          disabled={isVerifying || isResending}
          error={Boolean(codeError)}
          autoFocus
          className="justify-center"
        />
        {codeError && (
          <p className="text-center text-sm text-red-600">{codeError}</p>
        )}
      </div>

      <Button
        size="lg"
        variant="primary"
        className="w-full"
        disabled={isVerifying || code.length !== 6}
        onClick={handleVerify}
      >
        {isVerifying ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.verify.verifying")}
          </>
        ) : (
          t("account.verify.verify_button")
        )}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={handleResend}
          disabled={isResending || resendCooldown > 0}
          className="inline-flex items-center justify-center rounded-md text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isResending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("account.verify.resend")}
            </span>
          ) : (
            resendLabel
          )}
        </button>
        <div className="mt-2 space-y-1" aria-live="polite">
          {resendMessage && (
            <p className="text-sm text-slate-600">{resendMessage}</p>
          )}
          {resendError && <p className="text-sm text-red-600">{resendError}</p>}
        </div>
      </div>
    </AccountPageLayout>
  );
};

export default AccountLoginVerifyPage;
